import { BUNDLED_LOCALES, FALLBACK_LANGUAGE, SUPPORTED_LOCALES, UNKNOWN_BRAND_LOCALES } from './LocalizationManager';

/**
 * Languages a brand's users may switch between, primary first, English always present
 * as the fallback. A brand without a known set (legacy cache, unreachable
 * brand-config) offers UNKNOWN_BRAND_LOCALES.
 */
export function brandLanguages(brand) {
  const languages = (brand?.languages ?? []).filter((language) => SUPPORTED_LOCALES.includes(language));

  if (!languages.length) {
    return UNKNOWN_BRAND_LOCALES;
  }

  return languages.includes(FALLBACK_LANGUAGE) ? languages : [...languages, FALLBACK_LANGUAGE];
}

/**
 * The language a brand's screens open in: its primary language once that has a
 * translation bundle, until then the first bundled one — in practice English.
 */
export function defaultBrandLanguage(brand) {
  return brandLanguages(brand).find((language) => BUNDLED_LOCALES.includes(language)) ?? FALLBACK_LANGUAGE;
}

/**
 * Languages an operator surface may offer for a given outlet: what the brand offers,
 * plus the outlet's own configured language, that one first.
 *
 * The outlet language has to be in the set even when the brand does not list it. The
 * outlet language picker offers every language the platform ships, and per the ticket's
 * settings map that value is "the language customers see on the storefront and that
 * operator surfaces default to for this outlet". Gate it away and an outlet set to
 * Polish gets a Polish storefront and a German terminal — discarded by the very surface
 * it was configured to change, and not even offerable in the switcher. Brand alignment
 * still governs everything else, so a German session cannot leak onto a Spanish
 * partner's screens.
 */
export function operatorLanguages(brand, outletLanguage) {
  const offered = brandLanguages(brand);

  if (!outletLanguage || !SUPPORTED_LOCALES.includes(outletLanguage) || offered.includes(outletLanguage)) {
    return offered;
  }

  return [outletLanguage, ...offered];
}

/**
 * Keeps the current language when the brand offers it (a staff pick, the browser
 * language, `?lng=`), otherwise the brand default — a German session must not leak
 * onto a Spanish partner's screens.
 */
export function resolveBrandLanguage(brand, currentLanguage) {
  if (currentLanguage && brandLanguages(brand).includes(currentLanguage)) {
    return currentLanguage;
  }

  return defaultBrandLanguage(brand);
}
